import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { 
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, 
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle 
} from "../components/ui/alert-dialog";
import { 
  Clock, ChevronLeft, ChevronRight, AlertTriangle, 
  CheckCircle, Send, Shield, Eye, Sparkles, ThumbsUp, Zap, Star, Flame, Target
} from "lucide-react";
import IdentityVerification from "./IdentityVerification";

const MESSAGES = {
  streak: [
    { icon: Flame, message: "3 in a row! You're on fire!", color: "text-orange-500" },
    { icon: Zap, message: "5 correct streak! Unstoppable!", color: "text-amber-500" },
    { icon: Star, message: "Amazing streak! Keep it up!", color: "text-yellow-500" },
  ],
  milestone: [
    { icon: Target, message: "25% done! Great pace!", color: "text-blue-500" },
    { icon: Sparkles, message: "Halfway there! You're doing great!", color: "text-indigo-500" },
    { icon: ThumbsUp, message: "75% complete! Almost done!", color: "text-emerald-500" },
    { icon: Star, message: "Last few questions! Finish strong!", color: "text-purple-500" },
  ],
  general: [
    { icon: Sparkles, message: "Great progress! Keep going!", color: "text-amber-500" },
    { icon: ThumbsUp, message: "You're doing amazing!", color: "text-emerald-500" },
    { icon: Zap, message: "Stay focused, you've got this!", color: "text-blue-500" },
    { icon: Star, message: "Excellent work so far!", color: "text-indigo-500" },
    { icon: Sparkles, message: "You're making great progress!", color: "text-purple-500" },
    { icon: ThumbsUp, message: "Stay confident!", color: "text-cyan-500" },
  ]
};

const ExamInterface = () => {
  const { examId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  
  const [identityVerified, setIdentityVerified] = useState(false);
  const [examName, setExamName] = useState('');
  const [loading, setLoading] = useState(true);
  const [attempt, setAttempt] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [remainingTime, setRemainingTime] = useState(0);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [showTabWarning, setShowTabWarning] = useState(false);
  const [tabSwitchCount, setTabSwitchCount] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [encouragingMessage, setEncouragingMessage] = useState(null);
  const [correctStreak, setCorrectStreak] = useState(0);
  const [answeredCount, setAnsweredCount] = useState(0);
  
  const timerRef = useRef(null);
  const isExamActive = useRef(true);
  const shownMilestones = useRef(new Set());

  // Dynamic encouraging messages
  const showMessage = useCallback((msg) => {
    setEncouragingMessage(msg);
    setTimeout(() => setEncouragingMessage(null), 3000);
  }, []);

  useEffect(() => {
    if (!attempt || answeredCount === 0) return;
    const total = attempt.total_questions;
    const pct = (answeredCount / total) * 100;

    // Milestone messages
    if (pct >= 90 && !shownMilestones.current.has(90)) {
      shownMilestones.current.add(90);
      showMessage(MESSAGES.milestone[3]);
    } else if (pct >= 75 && !shownMilestones.current.has(75)) {
      shownMilestones.current.add(75);
      showMessage(MESSAGES.milestone[2]);
    } else if (pct >= 50 && !shownMilestones.current.has(50)) {
      shownMilestones.current.add(50);
      showMessage(MESSAGES.milestone[1]);
    } else if (pct >= 25 && !shownMilestones.current.has(25)) {
      shownMilestones.current.add(25);
      showMessage(MESSAGES.milestone[0]);
    }
  }, [answeredCount, attempt, showMessage]);

  // Streak messages
  useEffect(() => {
    if (correctStreak === 3) showMessage(MESSAGES.streak[0]);
    else if (correctStreak === 5) showMessage(MESSAGES.streak[1]);
    else if (correctStreak > 5 && correctStreak % 3 === 0) showMessage(MESSAGES.streak[2]);
  }, [correctStreak, showMessage]);

  // General periodic messages
  useEffect(() => {
    if (currentIndex > 0 && currentIndex % 7 === 0 && !encouragingMessage) {
      showMessage(MESSAGES.general[Math.floor(Math.random() * MESSAGES.general.length)]);
    }
  }, [currentIndex, encouragingMessage, showMessage]);

  const startExam = useCallback(async () => {
    try {
      const response = await axios.post(`${API}/candidate/exams/${examId}/start`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAttempt(response.data);
      setExamName(response.data.exam_name || '');
      setRemainingTime(response.data.remaining_seconds);
      setIdentityVerified(true);
      await fetchQuestion(0);
    } catch (error) {
      if (error.response?.data?.detail === "Exam already completed") {
        navigate(`/exam/${examId}/result`);
      } else if (error.response?.data?.detail === "Exam time expired") {
        toast.error("Exam time has expired");
        navigate(`/exam/${examId}/result`);
      } else if (error.response?.data?.detail === "Identity verification required before starting the exam") {
        // Need verification first - fetch exam name
        try {
          const examRes = await axios.get(`${API}/candidate/exams`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const examData = examRes.data.find(e => e.exam.id === examId);
          if (examData) setExamName(examData.exam.name);
        } catch (e) { /* ignore */ }
        setLoading(false);
        return;
      } else {
        toast.error(error.response?.data?.detail || "Failed to start exam");
        navigate("/dashboard");
      }
    } finally {
      setLoading(false);
    }
  }, [examId, token, navigate]);

  const fetchQuestion = async (index) => {
    try {
      const response = await axios.get(`${API}/candidate/exams/${examId}/question/${index}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentQuestion(response.data.question);
      setCurrentIndex(index);
      setSelectedAnswer(response.data.answered);
      setRemainingTime(response.data.remaining_seconds);
    } catch (error) {
      if (error.response?.data?.detail === "Exam time expired") {
        toast.error("Time's up! Exam auto-submitted.");
        navigate(`/exam/${examId}/result`);
      }
    }
  };

  const saveAnswer = async () => {
    if (selectedAnswer === null) return;
    try {
      await axios.post(`${API}/candidate/exams/${examId}/answer/${currentIndex}`, {
        question_id: currentQuestion.id,
        selected_answer: selectedAnswer
      }, { headers: { Authorization: `Bearer ${token}` } });
      setAnsweredCount(prev => prev + 1);
    } catch (error) {
      console.error("Failed to save answer", error);
    }
  };

  const submitExam = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await saveAnswer();
      await axios.post(`${API}/candidate/exams/${examId}/submit`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      isExamActive.current = false;
      toast.success("Exam submitted successfully!");
      navigate(`/exam/${examId}/result`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit exam");
      setSubmitting(false);
    }
  };

  const goToQuestion = async (index) => {
    await saveAnswer();
    await fetchQuestion(index);
  };
  const goNext = async () => { if (currentIndex < attempt.total_questions - 1) await goToQuestion(currentIndex + 1); };
  const goPrev = async () => { if (currentIndex > 0) await goToQuestion(currentIndex - 1); };

  // Log proctoring event
  const logProctoringEvent = async (eventType, details) => {
    try {
      await axios.post(`${API}/candidate/exams/${examId}/proctoring-event`, {
        event_type: eventType, details
      }, { headers: { Authorization: `Bearer ${token}` } });
    } catch (e) { /* silently fail */ }
  };

  // Timer
  useEffect(() => {
    if (remainingTime > 0 && isExamActive.current) {
      timerRef.current = setInterval(() => {
        setRemainingTime(prev => {
          if (prev <= 1) { clearInterval(timerRef.current); submitExam(); return 0; }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [remainingTime > 0]);

  // Tab switching detection with proctoring
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && isExamActive.current) {
        setTabSwitchCount(prev => prev + 1);
        setShowTabWarning(true);
        logProctoringEvent("tab_switch", "Document hidden");
      }
    };
    const handleBlur = () => {
      if (isExamActive.current) {
        setTabSwitchCount(prev => prev + 1);
        setShowTabWarning(true);
        logProctoringEvent("tab_switch", "Window blur");
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", handleBlur);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", handleBlur);
    };
  }, []);

  // Prevent context menu, copy/paste with proctoring
  useEffect(() => {
    const handleContextMenu = (e) => { e.preventDefault(); logProctoringEvent("right_click", "Context menu blocked"); };
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 'c' || e.key === 'v' || e.key === 'p')) {
        e.preventDefault();
        logProctoringEvent("copy_attempt", `Keyboard shortcut: ${e.key}`);
      }
    };
    document.addEventListener("contextmenu", handleContextMenu);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("contextmenu", handleContextMenu);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  useEffect(() => {
    startExam();
    return () => { isExamActive.current = false; clearInterval(timerRef.current); };
  }, [startExam]);

  const formatTime = (s) => `${Math.floor(s / 60).toString().padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`;
  const getTimerClass = () => remainingTime < 60 ? "timer-danger" : remainingTime < 300 ? "timer-warning" : "";

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center"><div className="spinner mx-auto mb-4" /><p className="text-slate-600">Loading exam...</p></div>
    </div>
  );

  // Identity verification gate
  if (!identityVerified && !attempt) {
    return (
      <IdentityVerification
        examId={examId}
        examName={examName}
        onVerified={() => {
          setIdentityVerified(true);
          setLoading(true);
          startExam();
        }}
        onSkip={() => {
          setIdentityVerified(true);
          setLoading(true);
          startExam();
        }}
      />
    );
  }

  return (
    <div className="exam-focus-mode bg-slate-50" data-testid="exam-interface">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-indigo-600" />
                <span className="font-semibold text-slate-900">{attempt?.exam_name}</span>
              </div>
              <div className="h-6 w-px bg-slate-200" />
              <span className="text-sm text-slate-500">Question {currentIndex + 1} of {attempt?.total_questions}</span>
            </div>
            <div className="flex items-center gap-6">
              {tabSwitchCount > 0 && (
                <div className="flex items-center gap-2 text-amber-600 bg-amber-50 px-3 py-1.5 rounded-lg">
                  <Eye className="w-4 h-4" /><span className="text-sm font-medium">Warnings: {tabSwitchCount}</span>
                </div>
              )}
              <div className={`flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-100 ${getTimerClass()}`}>
                <Clock className="w-5 h-5" />
                <span className={`exam-timer ${getTimerClass()}`} data-testid="exam-timer">{formatTime(remainingTime)}</span>
              </div>
              <Button onClick={() => setShowSubmitDialog(true)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="submit-exam-btn">
                <Send className="w-4 h-4 mr-2" />Submit Exam
              </Button>
            </div>
          </div>
          <div className="mt-4"><Progress value={((currentIndex + 1) / attempt?.total_questions) * 100} className="h-2" /></div>
        </div>
      </header>

      {/* Dynamic Encouraging Message */}
      {encouragingMessage && (
        <div className="fixed top-24 left-1/2 -translate-x-1/2 z-50 animate-fade-in">
          <div className="flex items-center gap-3 bg-white shadow-lg rounded-full px-6 py-3 border border-slate-100">
            <encouragingMessage.icon className={`w-5 h-5 ${encouragingMessage.color}`} />
            <span className="font-medium text-slate-800">{encouragingMessage.message}</span>
          </div>
        </div>
      )}

      <main className="max-w-4xl mx-auto px-6 py-8">
        {currentQuestion && (
          <Card className="question-card animate-fade-in" data-testid="question-card">
            <CardContent className="p-8">
              <div className="flex items-center justify-between mb-6">
                <span className="text-sm text-slate-500">Topic: <span className="font-medium text-slate-700">{currentQuestion.topic}</span></span>
                <span className="text-sm text-indigo-600 font-medium">Q{currentIndex + 1}</span>
              </div>
              <h2 className="text-xl font-semibold text-slate-900 mb-8 leading-relaxed" data-testid="question-text">{currentQuestion.question_text}</h2>
              <div className="space-y-3">
                {currentQuestion.options.map((option, index) => (
                  <div key={index} className={`option-card ${selectedAnswer === index ? 'selected' : ''}`} onClick={() => setSelectedAnswer(index)} data-testid={`option-${index}`}>
                    <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${selectedAnswer === index ? 'border-indigo-600 bg-indigo-600' : 'border-slate-300'}`}>
                      {selectedAnswer === index ? <CheckCircle className="w-5 h-5 text-white" /> : <span className="text-sm font-medium text-slate-500">{String.fromCharCode(65 + index)}</span>}
                    </div>
                    <span className={`text-base ${selectedAnswer === index ? 'text-indigo-900 font-medium' : 'text-slate-700'}`}>{option}</span>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between mt-10 pt-6 border-t border-slate-100">
                <Button variant="outline" onClick={goPrev} disabled={currentIndex === 0} data-testid="prev-btn">
                  <ChevronLeft className="w-4 h-4 mr-1" />Previous
                </Button>
                <div className="flex items-center gap-2">
                  {Array.from({ length: Math.min(attempt?.total_questions || 0, 10) }, (_, i) => {
                    const startIdx = Math.max(0, Math.min(currentIndex - 4, (attempt?.total_questions || 0) - 10));
                    const idx = startIdx + i;
                    return (
                      <button key={idx} onClick={() => goToQuestion(idx)} className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors ${idx === currentIndex ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-testid={`question-nav-${idx}`}>{idx + 1}</button>
                    );
                  })}
                </div>
                <Button onClick={goNext} disabled={currentIndex === attempt?.total_questions - 1} className="bg-indigo-600 hover:bg-indigo-700" data-testid="next-btn">
                  Next<ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        <p className="text-center text-slate-500 text-sm mt-6">Take your time and think through each question carefully.</p>
      </main>

      <AlertDialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Submit Exam?</AlertDialogTitle>
            <AlertDialogDescription>Are you sure? This cannot be undone. You have progressed to question {currentIndex + 1}.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-submit-btn">Continue Exam</AlertDialogCancel>
            <AlertDialogAction onClick={submitExam} className="bg-indigo-600 hover:bg-indigo-700" disabled={submitting} data-testid="confirm-submit-btn">
              {submitting ? "Submitting..." : "Submit Exam"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showTabWarning} onOpenChange={setShowTabWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-600"><AlertTriangle className="w-5 h-5" />Tab Switching Detected</AlertDialogTitle>
            <AlertDialogDescription>
              You have switched away from the exam tab. This activity is monitored and recorded. Multiple violations may result in disqualification.
              <br /><br /><strong>Tab switches: {tabSwitchCount}</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setShowTabWarning(false)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="acknowledge-warning-btn">
              I Understand, Continue Exam
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ExamInterface;
